/**
 * API Key Create Dialog Component
 *
 * Dialog for creating a new API key with form validation.
 * Shows the new key after creation (copy-to-clipboard).
 */

import { useState } from 'react';
import { Plus, Loader2, Copy, Check, AlertTriangle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useCreateApiKey } from '@/hooks/use-api-keys';
import { useToast } from '@/hooks/use-toast';
import { CLIENT_TYPES, CLIENT_TYPE_DETAILS } from '@/types/api-key';
import type { ClientType, CreateApiKeyResponse } from '@/types/api-key';

interface ApiKeyCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

/**
 * Form Schema
 * Validation schema for create form
 */
const createFormSchema = z.object({
  keyName: z.string().min(1, 'Key name is required').max(100, 'Key name too long'),
  clientType: z.enum(CLIENT_TYPES),
  maxRequests: z.number().int().min(1, 'Must be at least 1').optional(),
  windowMs: z.number().int().min(1000, 'Must be at least 1000ms').optional(),
});

type CreateFormValues = z.infer<typeof createFormSchema>;

/**
 * API Key Create Dialog
 * Renders form for creating new API key
 */
export function ApiKeyCreateDialog({ open, onOpenChange, onSuccess }: ApiKeyCreateDialogProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdKey, setCreatedKey] = useState<CreateApiKeyResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const createApiKeyMutation = useCreateApiKey();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CreateFormValues>({
    resolver: zodResolver(createFormSchema),
    defaultValues: {
      clientType: 'web',
    },
  });

  const watchedClientType = watch('clientType');

  const onSubmit = async (data: CreateFormValues) => {
    setIsSubmitting(true);
    try {
      const result = await createApiKeyMutation.mutateAsync({
        keyName: data.keyName,
        clientType: data.clientType,
        maxRequests: data.maxRequests,
        windowMs: data.windowMs,
      });

      setCreatedKey(result);

      toast({
        title: 'API Key Created',
        description: `${data.keyName} has been created successfully`,
      });

      onSuccess();
    } catch (error) {
      toast({
        title: 'Creation Failed',
        description: error instanceof Error ? error.message : 'Failed to create API key',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyKey = async () => {
    if (!createdKey?.data.apiKey) return;

    try {
      await navigator.clipboard.writeText(createdKey.data.apiKey);
      setCopied(true);
      toast({
        title: 'Copied!',
        description: 'API key copied to clipboard',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: 'Copy Failed',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      });
    }
  };

  const handleClose = () => {
    setCreatedKey(null);
    setCopied(false);
    reset();
    onOpenChange(false);
  };

  // If we have a created key, show it
  if (createdKey) {
    return (
      <Dialog open={true} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Check className="h-5 w-5" />
              API Key Created Successfully
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Save this key now!</AlertTitle>
              <AlertDescription>
                This is the only time you will see this key. Copy it and store it securely.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <label className="text-sm font-medium">API Key</label>
              <div className="flex gap-2">
                <code className="flex-1 p-3 bg-muted rounded-md text-sm font-mono break-all">
                  {createdKey.data.apiKey}
                </code>
                <Button variant="outline" size="icon" onClick={handleCopyKey}>
                  {copied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="bg-muted p-4 rounded-md space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Key Name:</span>
                <span className="font-medium">{createdKey.data.keyName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Client Type:</span>
                <span className="font-medium">{createdKey.data.clientType.toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Rate Limit:</span>
                <span className="font-mono text-xs">
                  {createdKey.data.maxRequests.toLocaleString()} /{' '}
                  {Math.round(createdKey.data.windowMs / 60000)} min
                </span>
              </div>
              {createdKey.data.expiresAt && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Expires:</span>
                  <span className="font-medium">
                    {new Date(createdKey.data.expiresAt).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button onClick={handleClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create API Key
          </DialogTitle>
          <DialogDescription>Create a new API key for client authentication</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="keyName">Key Name *</Label>
            <Input
              id="keyName"
              {...register('keyName')}
              placeholder="e.g., Production Web Client"
            />
            {errors.keyName && <p className="text-sm text-destructive">{errors.keyName.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="clientType">Client Type *</Label>
            <Select
              value={watchedClientType}
              onValueChange={(value: ClientType) => setValue('clientType', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select client type" />
              </SelectTrigger>
              <SelectContent>
                {CLIENT_TYPES.map(type => (
                  <SelectItem key={type} value={type}>
                    {CLIENT_TYPE_DETAILS[type].option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.clientType && (
              <p className="text-sm text-destructive">{errors.clientType.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Web keys expire in 90 days. Mobile, CMS and voice keys have extended validity.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="maxRequests">Max Requests (optional)</Label>
              <Input
                id="maxRequests"
                type="number"
                {...register('maxRequests', { valueAsNumber: true })}
                placeholder="Default: varies by type"
              />
              {errors.maxRequests && (
                <p className="text-sm text-destructive">{errors.maxRequests.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="windowMs">Window (ms) (optional)</Label>
              <Input
                id="windowMs"
                type="number"
                {...register('windowMs', { valueAsNumber: true })}
                placeholder="Default: 300000 (5 min)"
              />
              {errors.windowMs && (
                <p className="text-sm text-destructive">{errors.windowMs.message}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Key
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
